import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from 'amazon-cognito-identity-js';

// Cognito configuration from environment variables
const COGNITO_CONFIG = {
  region: process.env.VUE_APP_COGNITO_REGION || 'eu-central-1',
  userPoolId: process.env.VUE_APP_COGNITO_USER_POOL_ID,
  userPoolWebClientId: process.env.VUE_APP_COGNITO_CLIENT_ID,
};

const poolData = {
  UserPoolId: COGNITO_CONFIG.userPoolId,
  ClientId: COGNITO_CONFIG.userPoolWebClientId,
};

const userPool = new CognitoUserPool(poolData);

// I18n translations for error messages
const translations = {
  fi: {
    'Username cannot be empty': 'Käyttäjätunnus ei saa olla tyhjä.',
    'Username/client id combination not found.': 'Käyttäjätunnusta ei löytynyt.',
    'User does not exist.': 'Väärä käyttäjätunnus tai salasana.',
    'Incorrect username or password.': 'Väärä käyttäjätunnus tai salasana.',
    'Custom auth lambda trigger is not configured for the user pool.': 'Jotain meni pieleen. Ole hyvä ja yritä uudelleen.',
    'user.completeNewPasswordChallenge is not a function': 'Jotain meni pieleen. Ole hyvä ja palaa takaisin kirjautumisen alkuun.',
  },
  en: {},
};

let currentLanguage = 'en';

const CognitoService = {
  setLanguage(lang) {
    currentLanguage = lang;
  },

  translate(message) {
    if (!message) return message;
    const langTranslations = translations[currentLanguage] || translations.en;
    return langTranslations[message] || message;
  },

  /**
   * Sign in with username and password
   * @param {string} username
   * @param {string} password
   * @returns {Promise<{session?: object, user?: CognitoUser, challengeName?: string}>}
   */
  signIn(username, password) {
    return new Promise((resolve, reject) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: username,
        Password: password,
      });

      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool,
      });

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: session => {
          resolve({ session, user: cognitoUser });
        },
        onFailure: err => {
          reject(err);
        },
        newPasswordRequired: (userAttributes, requiredAttributes) => {
          // User needs to set a new password
          resolve({
            challengeName: 'NEW_PASSWORD_REQUIRED',
            user: cognitoUser,
            userAttributes,
            requiredAttributes,
          });
        },
        mfaRequired: (challengeName, challengeParameters) => {
          resolve({
            challengeName: 'SMS_MFA',
            user: cognitoUser,
            challengeParameters,
          });
        },
        totpRequired: (challengeName, challengeParameters) => {
          resolve({
            challengeName: 'SOFTWARE_TOKEN_MFA',
            user: cognitoUser,
            challengeParameters,
          });
        },
        mfaSetup: (challengeName, challengeParameters) => {
          resolve({
            challengeName: 'MFA_SETUP',
            user: cognitoUser,
            challengeParameters,
          });
        },
      });
    });
  },

  /**
   * Complete new password challenge
   * @param {CognitoUser} user
   * @param {string} newPassword
   * @param {object} requiredAttributes
   * @returns {Promise<object>}
   */
  completeNewPasswordChallenge(user, newPassword, requiredAttributes = {}) {
    return new Promise((resolve, reject) => {
      user.completeNewPasswordChallenge(newPassword, requiredAttributes, {
        onSuccess: session => {
          resolve({ session, user });
        },
        onFailure: err => {
          reject(err);
        },
        mfaRequired: (challengeName, challengeParameters) => {
          resolve({
            challengeName: 'SMS_MFA',
            user,
            challengeParameters,
          });
        },
        mfaSetup: (challengeName, challengeParameters) => {
          resolve({
            challengeName: 'MFA_SETUP',
            user,
            challengeParameters,
          });
        },
      });
    });
  },

  /**
   * Sign out the current user
   * @returns {Promise<void>}
   */
  signOut() {
    return new Promise(resolve => {
      const cognitoUser = userPool.getCurrentUser();
      if (cognitoUser) {
        cognitoUser.signOut();
      }
      resolve();
    });
  },

  /**
   * Send forgot password code
   * @param {string} username
   * @returns {Promise<object>}
   */
  forgotPassword(username) {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool,
      });

      cognitoUser.forgotPassword({
        onSuccess: data => {
          resolve(data);
        },
        onFailure: err => {
          reject(err);
        },
      });
    });
  },

  /**
   * Confirm new password with code
   * @param {string} username
   * @param {string} code
   * @param {string} newPassword
   * @returns {Promise<void>}
   */
  confirmPassword(username, code, newPassword) {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool,
      });

      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: () => {
          resolve();
        },
        onFailure: err => {
          reject(err);
        },
      });
    });
  },

  /**
   * Resend signup confirmation code
   * @param {string} username
   * @returns {Promise<object>}
   */
  resendSignUp(username) {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool,
      });

      cognitoUser.resendConfirmationCode((err, result) => {
        if (err) {
          reject(err);
        } else {
          resolve(result);
        }
      });
    });
  },

  /**
   * Get current authenticated session (and refresh if needed)
   * @returns {Promise<object|null>}
   */
  getCurrentSession() {
    return new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser();

      if (!cognitoUser) {
        resolve(null);
        return;
      }

      cognitoUser.getSession((err, session) => {
        if (err) {
          reject(err);
          return;
        }

        if (!session || !session.isValid()) {
          resolve(null);
          return;
        }

        resolve(session);
      });
    });
  },

  /**
   * Get current authenticated user
   * @returns {CognitoUser|null}
   */
  getCurrentUser() {
    return userPool.getCurrentUser();
  },

  /**
   * Change password for current user
   * @param {string} oldPassword
   * @param {string} newPassword
   * @returns {Promise<void>}
   */
  changePassword(oldPassword, newPassword) {
    return new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser();

      if (!cognitoUser) {
        reject(new Error('No user is currently authenticated'));
        return;
      }

      cognitoUser.getSession((err, session) => {
        if (err || !session || !session.isValid()) {
          reject(err || new Error('Session is not valid'));
          return;
        }

        cognitoUser.changePassword(oldPassword, newPassword, (err, result) => {
          if (err) {
            reject(err);
          } else {
            resolve(result);
          }
        });
      });
    });
  },

  /**
   * Extract session info from Cognito session
   * @param {object} session
   * @returns {object}
   */
  extractSessionInfo(session) {
    const idToken = session.getIdToken();
    const accessToken = session.getAccessToken();
    const refreshToken = session.getRefreshToken();

    return {
      sub: idToken.payload.sub,
      email: idToken.payload.email,
      roles: '',
      accessToken: accessToken.getJwtToken(),
      idToken: idToken.getJwtToken(),
      refreshToken: refreshToken.getToken(),
      accessTokenExpires: new Date(accessToken.payload.exp * 1000),
    };
  },
};

export default CognitoService;
